<template>
  <div class="mobile-map-wrapper">
    <div class="mobile-map-container" ref="mapContainerRef"></div>
  </div>
</template>

<script>
import * as echarts from 'echarts'
import axios from 'axios'
import { getMapTheme } from '../utils/mapTheme'
import { useThemeStore } from '../store/theme'
import { dashboardStationPayload } from '../utils/dashboardNavigation.mjs'

// 移动端专用的地图缓存
let isChinaMapRegistered = false
let chinaGeoJsonCache = null
const featureBoundsCache = new Map()
const MAP_MOTION_DURATION = 220

export default {
  name: 'MobileMapComponent',
  props: {
    stations: {
      type: Array,
      default: () => []
    }
  },
  emits: ['station-click', 'province-click'],
  setup() {
    return { themeStore: useThemeStore() }
  },
  data() {
    return {
      chartInstance: null,
      selectedProvince: null,
      selectedStation: null,
      animationTimer: null
    }
  },
  created() {
    // Non-reactive state to prevent infinite zoom loops
    this._currentCenter = [126.192, 43.723]
    this._currentZoom = 6
  },
  watch: {
    stations: {
      handler() {
        if (this.chartInstance) {
          this.updateChart()
        }
      }
    },
    'themeStore.theme'() {
      if (this.chartInstance) {
        this.renderChart()
      }
    }
  },
  async mounted() {
    await this.loadGeoJson()
    this.initChart()
    window.addEventListener('resize', this.handleResize)
  },
  beforeUnmount() {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer)
      this.animationTimer = null
    }
    if (this.chartInstance) {
      this.chartInstance.dispose()
      this.chartInstance = null
    }
    window.removeEventListener('resize', this.handleResize)
  },
  methods: {
    async loadGeoJson() {
      if (!isChinaMapRegistered) {
        try {
          // 1. 优先尝试根目录加载 (适配生产环境 www.microquake.autosquare.org.cn)
          const res = await axios.get('/china.json')
          chinaGeoJsonCache = Object.freeze(res.data)
          echarts.registerMap('china', JSON.parse(JSON.stringify(chinaGeoJsonCache)))
          isChinaMapRegistered = true
        } catch (err) {
          // 2. 如果失败，尝试带前缀的路径 (适配本地开发环境 /geofusion/)
          try {
            const res = await axios.get('/geofusion/china.json')
            chinaGeoJsonCache = Object.freeze(res.data)
            echarts.registerMap('china', JSON.parse(JSON.stringify(chinaGeoJsonCache)))
            isChinaMapRegistered = true
          } catch (err2) {
             console.error('[MobileMapComponent] 本地 GeoJSON 加载失败，尝试网络回退:', err2)
             // 3. 最后尝试网络资源兜底
            try {
              const res = await axios.get(
                'https://geo.datav.aliyun.com/areas_v3/bound/100000_full.json'
              )
              chinaGeoJsonCache = Object.freeze(res.data)
              echarts.registerMap('china', JSON.parse(JSON.stringify(chinaGeoJsonCache)))
              isChinaMapRegistered = true
            } catch (err3) {
              console.error('[MobileMapComponent] 所有 GeoJSON 加载均失败:', err3)
            }
          }
        }
      }

      if (chinaGeoJsonCache && this.selectedProvince) {
        const feature = chinaGeoJsonCache.features.find(
          (f) => f.properties.name === this.selectedProvince
        )
        if (feature) {
          const bounds = this.getFeatureBounds(feature)
          if (bounds) {
            this._currentCenter = bounds.center
            this._currentZoom = bounds.zoom
            this.currentZoomLevel = Math.round(bounds.zoom * 10) / 10
          }
        }
      }
    },

    initChart() {
      const el = this.$refs.mapContainerRef
      if (!el) return
      this.chartInstance = echarts.init(el)
      this.renderChart()
      this.bindEvents()
    },

    renderChart() {
      if (!this.chartInstance) return
      const option = this.buildOption()
      this.chartInstance.setOption(option, { notMerge: true })
    },

    updateChart() {
      if (!this.chartInstance) return
      const { normal, alert } = this.getStationSeriesData()
      this.chartInstance.setOption({
        series: [
          { id: 'mobile-normal-stations', data: normal },
          { id: 'mobile-alert-stations', data: alert },
          { id: 'mobile-monitoring-stations', data: [] }
        ]
      }, { notMerge: false, lazyUpdate: true })
    },

    getStationSeriesData() {
      const normal = []
      const alert = []
      this.stations.forEach((station) => {
        if (station.status && station.status !== 'normal') {
          alert.push(station)
        } else {
          normal.push(station)
        }
      })
      return { normal, alert }
    },

    buildOption() {
      const theme = getMapTheme()
      const { normal, alert } = this.getStationSeriesData()
      const commonSeries = {
        coordinateSystem: 'geo',
        symbolSize: 12,
        progressive: 100,
        progressiveThreshold: 200,
        itemStyle: {
          color: (params) => {
            const status = (params && params.data) ? params.data.status : 'normal'
            return this.getStatusColor(status)
          },
          shadowBlur: 8,
          shadowColor: theme.scatterShadow
        },
        label: {
          formatter: '{b}',
          position: 'right',
          show: false,
          color: theme.scatterLabel,
          fontSize: 10,
          textBorderColor: theme.scatterLabelBorder,
          textBorderWidth: 1
        }
      }
      return {
        backgroundColor: 'transparent',
        tooltip: {
          trigger: 'item',
          backgroundColor: theme.tooltipBg,
          borderColor: theme.tooltipBorder,
          borderWidth: 1,
          textStyle: { color: theme.tooltipText, fontSize: 12 },
          formatter: (params) => {
            if (params.componentType === 'series' && ['scatter', 'effectScatter'].includes(params.seriesType)) {
              if (!params.data) return null
              const statusMap = {
                normal: '<span style="color:#10b981">● 正常</span>',
                yellow: '<span style="color:#eab308">● 黄色预警</span>',
                orange: '<span style="color:#f97316">● 橙色预警</span>',
                red: '<span style="color:#ef4444">● 红色预警</span>'
              }
              const s = params.data.status || 'normal'
              return `
                <div style="padding: 4px 2px;">
                  <strong style="color: ${theme.tooltipAccent}">${params.name || '未知站点'}</strong><br/>
                  状态: ${statusMap[s] || s}<br/>
                  GPS: ${params.value && params.value.length >= 2 ? params.value[0].toFixed(4) : '--'}, 
                       ${params.value && params.value.length >= 2 ? params.value[1].toFixed(4) : '--'}
                </div>
              `
            }
            return null
          }
        },
        geo: {
          map: 'china',
          roam: true,
          selectedMode: 'single',
          zoom: this._currentZoom,
          center: this._currentCenter,
          itemStyle: {
            areaColor: theme.areaColor,
            borderColor: theme.borderColor,
            borderWidth: theme.borderWidth
          },
          label: {
            show: true,
            color: theme.labelColor,
            fontSize: theme.labelFontSize - 1
          },
          emphasis: {
            itemStyle: {
              areaColor: theme.areaHover,
              borderColor: theme.borderActive,
              borderWidth: theme.borderActiveWidth
            },
            label: {
              show: true,
              color: theme.labelHover,
              fontSize: 11
            }
          },
          select: {
            itemStyle: {
              areaColor: theme.areaSelected,
              borderColor: theme.borderActive,
              borderWidth: theme.borderActiveWidth,
              shadowBlur: 18,
              shadowColor: theme.selectedShadow
            },
            label: {
              show: true,
              color: theme.labelHover,
              fontWeight: 'bold',
              fontSize: 12
            }
          },
          regions: this.selectedProvince ? [{ name: this.selectedProvince, selected: true }] : []
        },
        series: [
          {
            ...commonSeries,
            id: 'mobile-normal-stations',
            name: '正常监测站',
            type: 'scatter',
            data: normal,
            zlevel: 1
          },
          {
            ...commonSeries,
            id: 'mobile-alert-stations',
            name: '告警监测站',
            type: 'effectScatter',
            data: alert,
            showEffectOn: 'render',
            rippleEffect: {
              brushType: 'stroke',
              scale: 2,
              period: 6
            },
            zlevel: 2
          },
          {
            ...commonSeries,
            id: 'mobile-monitoring-stations',
            name: '监测站',
            type: 'effectScatter',
            data: [],
            showEffectOn: 'render',
            rippleEffect: {
              brushType: 'stroke',
              scale: 2,
              period: 6
            },
            zlevel: 3
          }
        ]
      }
    },

    getStatusColor(status) {
      const map = {
        red: '#ef4444',
        orange: '#f97316',
        yellow: '#eab308',
        normal: '#10b981'
      }
      return map[status] || '#10b981'
    },

    bindEvents() {
      if (!this.chartInstance) return
      this.chartInstance.off('click')
      this.chartInstance.off('geoselectchanged')
      this.chartInstance.off('georoam')

      // 同步拖拽/缩放状态
      this.chartInstance.on('georoam', () => {
        const geoOpt = this.chartInstance.getOption().geo
        if (geoOpt && geoOpt[0]) {
          this._currentCenter = [...geoOpt[0].center]
          this._currentZoom = geoOpt[0].zoom
        }
      })

      // 站点点击
      this.chartInstance.on('click', 'series', (params) => {
        if (['scatter', 'effectScatter'].includes(params.seriesType) && params.value && Array.isArray(params.value) && params.value.length >= 2) {
          const stationId = params.data?.sid_no
          if (this.selectedStation === stationId) {
            this.selectedStation = null
            this.zoomToProvince(this.selectedProvince || '全国')
            this.$emit('province-click', { name: this.selectedProvince || '全国' })
          } else {
            this.selectedStation = stationId
            this.animateMapView([params.value[0], params.value[1]], 10)
            this.$emit('station-click', dashboardStationPayload(params.data, params.value[0], params.value[1]))
          }
        }
      })

      // 省份选择
      this.chartInstance.on('geoselectchanged', (params) => {
        const selected = params.allSelected[0]
        if (selected && selected.name && selected.name.length > 0) {
          const provinceName = selected.name[0]
          if (this.selectedProvince === provinceName) {
            this.selectedProvince = null
            this.selectedStation = null
            this.chartInstance.dispatchAction({ type: 'geoUnSelect', name: provinceName })
            this.resetMapView()
            this.$emit('province-click', { name: '全国' })
          } else {
            this.selectedProvince = provinceName
            const feature = chinaGeoJsonCache.features.find(f => f.properties.name === provinceName)
            const bounds = feature ? this.getFeatureBounds(feature) : null
            this.zoomToProvince(provinceName)
            this.$emit('province-click', { name: provinceName, center: bounds ? bounds.center : null })
          }
        } else {
          this.selectedProvince = null
          this.resetMapView()
          this.$emit('province-click', { name: '全国' })
        }
      })
    },

    zoomToProvince(provinceName) {
      if (!chinaGeoJsonCache) return
      const feature = chinaGeoJsonCache.features.find(f => f.properties.name === provinceName)
      if (!feature) return
      const bounds = this.getFeatureBounds(feature)
      if (!bounds) return
      this.animateMapView(bounds.center, bounds.zoom)
    },

    getFeatureBounds(feature) {
      const featureName = feature?.properties?.name
      if (featureName && featureBoundsCache.has(featureName)) {
        return featureBoundsCache.get(featureName)
      }
      const coords = feature.geometry.coordinates
      let minX = Infinity, maxX = -Infinity
      let minY = Infinity, maxY = -Infinity
      let hasPoints = false

      const processCoords = (arr) => {
        if (typeof arr[0] === 'number') {
          hasPoints = true
          if (arr[0] < minX) minX = arr[0]
          if (arr[0] > maxX) maxX = arr[0]
          if (arr[1] < minY) minY = arr[1]
          if (arr[1] > maxY) maxY = arr[1]
        } else {
          arr.forEach(processCoords)
        }
      }

      processCoords(coords)
      if (!hasPoints) return null

      const center = [(minX + maxX) / 2, (minY + maxY) / 2]
      const width = maxX - minX
      const height = maxY - minY
      const paddingFactor = 0.8
      const scaleW = 75 / (width || 1)
      const scaleH = 50 / (height || 1)
      let zoom = Math.min(scaleW, scaleH) * paddingFactor
      zoom = Math.max(1.5, Math.min(zoom, 12))
      const bounds = { center, zoom }
      if (featureName) featureBoundsCache.set(featureName, bounds)
      return bounds
    },

    resetMapView() {
      if (!this.chartInstance) return
      this.animateMapView([104.114129, 37.550339], 1.2)
    },

    animateMapView(targetCenter, targetZoom, duration = MAP_MOTION_DURATION) {
      if (!this.chartInstance) return
      const el = this.$refs.mapContainerRef
      if (!el) return

      if (this.animationTimer) {
        clearTimeout(this.animationTimer)
        this.animationTimer = null
      }

      el.style.transition = 'none'
      el.style.transform = 'none'
      el.style.opacity = '1'

      this._currentCenter = [...targetCenter]
      this._currentZoom = targetZoom

      try {
        this.chartInstance.dispatchAction({ type: 'hideTip' })
      } catch (e) {
        // ignore tooltip cleanup failures
      }

      this.chartInstance.setOption({
        geo: {
          center: targetCenter,
          zoom: targetZoom
        },
        animationDurationUpdate: duration,
        animationEasingUpdate: 'cubicOut'
      }, { notMerge: false, lazyUpdate: false })

      this.animationTimer = setTimeout(() => {
        this.animationTimer = null
      }, duration + 20)
    },

    zoomToStation(station) {
      if (!station || !station.value) return
      const [lon, lat] = station.value
      // Zoom into station with a specific level (e.g., 10)
      this.animateMapView([lon, lat], 10)
      this.selectedStation = station.sid_no
    },

    handleResize() {
      if (this.chartInstance) {
        this.chartInstance.resize()
      }
    }
  }
}
</script>

<style scoped>
.mobile-map-wrapper {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.mobile-map-container {
  width: 100%;
  height: 100%;
  min-height: 300px;
  display: block;
  touch-action: none;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
  pointer-events: auto;
  will-change: transform;
}
</style>
